import { Attachment, LocalShipping } from "@mui/icons-material";
import ClearIcon from "@mui/icons-material/Clear";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { useHistoryState } from "@uidotdev/usehooks";
import cuid from "cuid";
import type React from "react";
import { useContext, useRef, useState } from "react";
import { useDrop } from "react-aria";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import FilePreview from "@/components/FeedItem/FilePreview";
import PaperAirplane from "@/components/Icons/PaperAirplane";
import SendIcon from "@/components/Icons/Send";
import TemplateIcon from "@/components/Icons/TemplateIcon";
import TextMessageInput from "@/components/TextMessageInput";
import AudioLevelIndicator from "@/components/Visualization/AudioLevelIndicator.tsx";
import { ampli } from "@/config";
import useLocationChange from "@/hooks/useLocationChange";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import Locator from "@/locator";
import { publishToWorkspaceFeed } from "@/models/actions/clientActions";
import { sendContentEvent } from "@/models/contentEvents";
import { DataContext } from "@/models/DataProvider";
import { FeedContext } from "@/models/FeedContextProvider";
import { CurrentFeedContext } from "@/models/StateProviders/currentFeedProvider";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { actions, TelemetryContext } from "@/models/TelemetryProvider";
import { UppyContext } from "@/models/UppyContextProvider";
import { UxContext } from "@/models/UxStateProvider";
import { fileUploadConfig } from "@/utils/config";
import TemplatesModal from "../Templates/TemplatesModal.tsx";
import UploadFileButton from "../UploadFiles/UploadFileButton";

type PastedFile = File & {
  url: string;
  name: string;
  data: File | Blob;
  fileId: string;
};

const ttsPlaceholder = "Start typing your message";

export default function TTSForm({
  disableControls,
  makeCallButton,
  makeRecording,
  recordingProps,
  cancelRecording,
  saveRecording,
}: {
  disableControls?: boolean;
  makeCallButton?: React.ReactNode;
  makeRecording?: React.ReactNode;
  cancelRecording?: () => void;
  saveRecording?: () => void;
  recordingProps?: {
    processing: boolean;
    progress: number;
    disabled: boolean;
    showAudioLevels: boolean;
    audioLevel: number;
    time: string;
    uploading: boolean;
    color: string;
    uploadProgress: number;
  };
}) {
  const theme = useTheme();
  const { preferredLanguage } = useContext(DataContext);
  const { currentFeed, currentFeedId } = useContext(CurrentFeedContext);
  const { currentWorkspaceId, isWorkspaceLimitedMember } = useContext(WorkspaceContext);
  const { setTemplatesModal, templatesModal, setStatus: setFeedStatus, scrollToBottomOfFeed } = useContext(FeedContext);
  const { uppyClient } = useContext(UppyContext);
  const { isSmUp, setFleetMessageModalOpen } = useContext(UxContext);
  const {
    state: textState,
    set: setText,
    undo: undoText,
    redo: redoText,
    clear: clearText,
    canUndo: canUndoText,
    canRedo: canRedoText,
  } = useHistoryState({ textInputValue: "" });
  const [loading, setLoading] = useState<boolean>(false);
  const [files, setFiles] = useState<PastedFile[]>([]);
  const [groupId, setGroupId] = useState<string>(cuid());
  const textInputRef = useRef(null);
  const limitedMember = isWorkspaceLimitedMember();
  const { trackAction } = useContext(TelemetryContext);

  const workspaceId = currentWorkspaceId;
  const feedId = currentFeedId;

  const maxChars = 5000;

  const exceededCharLimit = textState.textInputValue.length > maxChars;
  const canSendMessage = (textState.textInputValue?.length > 0 || files.length > 0) && !exceededCharLimit;

  const disabled = loading || disableControls;
  const fileTypes =
    fileUploadConfig.allowedMimeTypes.length > 0
      ? fileUploadConfig.allowedMimeTypes?.filter(
          (at) => at.startsWith("image") || at.startsWith("application") || at.startsWith("text"),
        )
      : ["image/jpeg", "image/png", "image/svg+xml", "image/webp", "image/gif", "image/bmp", "application/pdf"];

  useLocationChange(() => {
    clearText();
  });

  const removeFile = (index) => {
    if (files.length > 0) {
      const newFiles = files.filter((file, i) => i !== index);
      if (uppyClient) {
        const file = files[index];
        uppyClient.removeFile(file?.fileId);
      }
      setFiles(newFiles);
    }
  };

  const reset = () => {
    setLoading(false);
    clearText();
    setFiles([]);
    uppyClient?.setOptions({ restrictions: { maxNumberOfFiles: 1 } });
    setGroupId(cuid());
    setTimeout(() => textInputRef.current?.focus(), 50);
  };

  // Guarded so the presses that land while a message is going out are dropped: the draft is only cleared once the
  // publish returns, and every entry mints its own ids, so a second entry is a second message to the driver.
  const sendTextMessage = useSingleFlight(async () => {
    if (canSendMessage) {
      const contentId = cuid();
      try {
        setLoading(true);
        setFeedStatus({ message: "Sending message...", severity: "info" });
        if (textState.textInputValue.length > 0) {
          ampli.textToSpeechMessageSend();

          sendContentEvent({
            workspaceId,
            contentId,
            step: "client_uploading",
            status: "started",
            feedId,
            context: {
              type: "tts_message",
              characterLength: textState.textInputValue?.length ?? 0,
            },
          }).then(() => {});

          const itemId = cuid();
          trackAction(actions.feedItemTextTts({ id: itemId, feedId }), {
            workspaceId,
            feedId,
            contentId,
            itemId,
            text: textState?.textInputValue,
            preferredLanguage,
            isSilent: currentFeed?.isSilent,
          });
          trackAction(actions.feedItemAudioTts({ id: itemId, feedId }), {
            workspaceId,
            feedId,
            contentId,
            itemId,
            text: textState?.textInputValue,
            preferredLanguage,
            isSilent: currentFeed?.isSilent,
          });

          await publishToWorkspaceFeed({
            workspaceId,
            feedId,
            contentId,
            groupId,
            itemId,
            text: textState?.textInputValue,
            preferredLanguage,
            isSilent: currentFeed?.isSilent,
          });

          sendContentEvent({
            workspaceId,
            contentId,
            step: "client_uploading",
            status: "finished",
            feedId,
            context: {
              type: "tts_message",
              characterLength: textState.textInputValue?.length ?? 0,
            },
          }).then(() => {});
        }

        if (files.length > 0) {
          // Start the upload
          await uppyClient?.upload();
        }
        setFeedStatus(null);
        // after sending message, scroll to bottom of the feed if present
        scrollToBottomOfFeed();
      } catch (error) {
        setFeedStatus({ message: "Failed to send message", severity: "error" });
        sendContentEvent({
          workspaceId,
          contentId,
          step: "client_uploading",
          status: "failed",
          feedId,
          context: {
            type: "tts_message",
            characterLength: textState.textInputValue?.length ?? 0,
          },
          error,
        }).then(() => {});
      } finally {
        reset();
      }
    }
  });

  const handleNewFileAdded = (file: File) => {
    if (file) {
      const url = URL.createObjectURL(file);
      const newFile = {
        url,
        name: file.name,
        type: file.type,
        data: file,
      } as PastedFile;
      const addedId = uppyClient.addFile({
        source: "TTSForm",
        meta: { groupId: groupId },
        ...newFile,
      });
      newFile.fileId = addedId || "";
      setFiles((prev) => [...prev, newFile]);
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const items = Array.from((event.clipboardData || event.originalEvent.clipboardData).items) as DataTransferItem[];

    if (items) {
      uppyClient?.setOptions({ restrictions: { maxNumberOfFiles: null } });
      items.map(async (item) => {
        if ((item.kind === "file" || item.kind === "image") && fileTypes.includes(item.type)) {
          const file = item.getAsFile();
          handleNewFileAdded(file);
        }

        // Only accept plain text values for this input type
        if (item.kind === "string" && item.type === "text/plain") {
          const cursorStartPosition = event.target.selectionStart || 0;
          const cursorEndPosition = event.target.selectionEnd || textState?.textInputValue?.length;
          item.getAsString((text) => {
            handleTextInput(
              textState?.textInputValue.slice(0, cursorStartPosition) +
                text +
                textState?.textInputValue.slice(cursorEndPosition),
            );
            // "setTimeout" to update caret after setting the text
            window.requestAnimationFrame(() => {
              textInputRef.current?.setSelectionRange(
                cursorStartPosition + text.length,
                cursorStartPosition + text.length,
              );
            });
          });
        }
      });
    }
  };

  const handleFleetMessageOpen = () => {
    ampli.openFleetMessage({ workspaceId });
    setFleetMessageModalOpen(true);
  };

  const { dropProps } = useDrop({
    ref: textInputRef,
    async onDrop(e) {
      console.log("onDrop", e);
      if (e.items) {
        uppyClient?.setOptions({ restrictions: { maxNumberOfFiles: null } });
        e.items.map(async (item) => {
          if (item.kind === "file" && fileTypes.includes(item.type)) {
            const file = await item.getFile();
            handleNewFileAdded(file);
          }

          if (item.kind === "text") {
            const textType = Array.from(item.types.values())?.[0];
            const text = await item.getText(textType || "text/plain");
            handleTextInput(textState?.textInputValue + text);
          }
        });
      }
    },
  });

  const handleKeyDown = async (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement | HTMLDivElement>) => {
    switch (event.code) {
      case "Enter":
      case "NumpadEnter":
        if (event.shiftKey) break;
        event.preventDefault();
        await sendTextMessage();
        break;
      case "KeyZ":
        if (event.ctrlKey || event.metaKey) {
          if (event.shiftKey) {
            if (canRedoText) {
              redoText();
            }
          } else {
            if (canUndoText) {
              undoText();
            }
          }
        }
        break;
    }
  };

  const handleTextInput = (text: string) => {
    setText({ textInputValue: text });
  };

  const handleTemplateSelection = (template: string) => {
    handleTextInput(template);
    setTimeout(() => textInputRef.current?.focus(), 50);
    setTemplatesModal(false);
  };

  const handleOpenTemplates = () => {
    setTemplatesModal(() => true);
    ampli.openTemplatesFeed();
  };

  const hasAudioState =
    recordingProps?.progress > 0 || recordingProps?.uploading || recordingProps?.processing || recordingProps?.disabled;

  const footerButtonSx = {
    display: "inline-flex",
    alignItems: "center",
    gap: 1,
    width: "auto",
    minWidth: "none",
    color: "var(--color-zinc-300)",
    textTransform: "none",
    letterSpacing: "0.05em",
    bgcolor: "var(--color-zinc-700)",
    borderRadius: 2,
    fontSize: "1rem",
    px: 1,
    py: 0.5,
    "&:hover": { color: "#fff", bgcolor: "var(--color-zinc-600)" },
  } as const;

  return (
    <>
      <Box
        sx={{
          bgcolor: "var(--color-zinc-800)",
          borderRadius: 1,
          border: "1px solid var(--color-zinc-400)",
          p: 1,
          width: "100%",
          height: "100%",
          position: "relative",
        }}
      >
        {hasAudioState && (
          <Box
            sx={{
              position: "absolute",
              zIndex: 30,
              left: 0,
              top: 0,
              width: "100%",
              height: "100%",
              bgcolor: "rgba(39, 39, 42, 0.8)",
              p: 2,
              borderRadius: 1,
              backdropFilter: "blur(2px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            style={{ boxShadow: `0 0 0 2px ${recordingProps?.color}` }}
          >
            {recordingProps.uploadProgress > 0 && (
              <Box sx={{ textAlign: "center", minWidth: 200 }}>
                <Box>Uploading {recordingProps.uploadProgress}%</Box>
                <Box
                  sx={{
                    bgcolor: "var(--color-zinc-700)",
                    p: 0.5,
                    borderRadius: 1,
                    mt: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      height: 4,
                      background: "linear-gradient(90deg, #4ade80, #16a34a)",
                      borderRadius: 1,
                      width: `${recordingProps.uploadProgress}%`,
                      transition: "width 0.2s ease-out",
                    }}
                  />
                </Box>
              </Box>
            )}

            {recordingProps.processing && recordingProps.uploadProgress === 0 && (
              <Box sx={{ textAlign: "center" }}>Processing audio...</Box>
            )}

            {!recordingProps.uploading &&
              !recordingProps.processing &&
              recordingProps.disabled &&
              recordingProps.progress === 0 && <Box sx={{ textAlign: "center" }}>Processing Request</Box>}

            {recordingProps.progress > 0 && (
              <>
                <Box sx={{ textAlign: "center" }}>
                  <Box
                    sx={{
                      bgcolor: "var(--color-brand-primary-500)",
                      color: "#fff",
                      p: 1,
                      borderRadius: "9999px",
                      mb: 1,
                    }}
                  >
                    <AudioLevelIndicator
                      audioLevel={recordingProps.audioLevel}
                      color={theme.palette.primary.main}
                      size={30}
                    />
                  </Box>
                  <Box>{recordingProps.time}</Box>
                </Box>
                <Button
                  type="button"
                  onClick={() => cancelRecording()}
                  sx={{
                    position: "absolute",
                    right: "110px",
                    bottom: "11px",
                    boxShadow: "0 0 0 2px var(--color-zinc-500)",
                    borderRadius: "50%",
                    minWidth: 0,
                    width: "auto",
                    bgcolor: "var(--color-zinc-700)",
                    "&:hover": { bgcolor: "var(--color-zinc-600)" },
                  }}
                >
                  <ClearIcon role="img" sx={{ fontSize: 20 }} />
                </Button>
                <Button
                  type="button"
                  onClick={() => saveRecording()}
                  sx={{
                    position: "absolute",
                    right: "52px",
                    bottom: "11px",
                    boxShadow: "0 0 0 2px #4ade80",
                    borderRadius: "50%",
                    minWidth: 0,
                    width: "auto",
                    bgcolor: "#16a34a",
                    "&:hover": { bgcolor: "#22c55e" },
                  }}
                >
                  <SendIcon width={18} height={18} />
                </Button>
              </>
            )}
          </Box>
        )}
        <Box
          data-sb={TestIds.feed.composer}
          sx={{
            borderBottom: "1px solid var(--color-zinc-400)",
            pb: 1,
          }}
        >
          <TextMessageInput
            inputId="tts-form-text-input"
            inputRef={textInputRef}
            inputProps={{
              autoFocus: true,
              onKeyDown: (e) => handleKeyDown(e),
              onPaste: (e) => handlePaste(e),
            }}
            sx={{
              padding: 0,
              width: "100%",
              ".MuiInputBase-input": {
                margin: 0,
                border: "none",
                borderRadius: 0,
                minHeight: 0,
                fontSize: "1rem",
                lineHeight: 1.5,
                // Keep vertical padding at 0 so MUI's maxRows math (which adds
                // padding to every counted row) clamps the textarea at exactly
                // 8 rows before scrolling.
                py: 0,
                px: 0.5,
              },
            }}
            minRows={4}
            maxRows={8}
            placeholder={ttsPlaceholder}
            aria-label={Locator.feed.input.tts}
            disabled={disabled}
            textInputValue={textState?.textInputValue}
            textInputHandler={handleTextInput}
            showHelperText={false}
          />
        </Box>
        <Box
          className="tts-helper"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            fontSize: "0.75rem",
            color: "var(--color-zinc-300)",
            py: 0.5,
          }}
        >
          <Box
            sx={{
              minWidth: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <Box component="span" sx={{ mr: 1.5 }}>
              Enter to send message
            </Box>
            <Box component="span">Shift+Enter to create new line</Box>
          </Box>
          <Box
            component="span"
            sx={{
              flexShrink: 0,
              whiteSpace: "nowrap",
              color: exceededCharLimit ? theme.palette.error.main : "var(--color-zinc-400)",
            }}
          >
            {textState?.textInputValue?.length} / {maxChars}
          </Box>
        </Box>
        {files.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              overflowX: "auto",
              px: 0.5,
              py: 0.5,
            }}
          >
            {files.map((file, index) => (
              <FilePreview
                key={`file-preview-${file.name}`}
                index={index}
                url={file.url}
                type={file.type}
                name={file.name}
                disabledRemove={disabled}
                removeFileCallback={(i) => removeFile(i)}
              />
            ))}
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            pt: 0.5,
          }}
        >
          <Box
            className="footer-buttons"
            sx={{
              flexGrow: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Button type="button" className="footer-button" onClick={handleOpenTemplates} sx={footerButtonSx}>
              <span className="icon">
                <TemplateIcon />
              </span>
              <span className="text">Templates</span>
            </Button>

            <UploadFileButton
              button={
                <Button type="button" className="footer-button" sx={footerButtonSx}>
                  <span className="icon">
                    <Attachment role="presentation" />
                  </span>
                  <span className="text">Files</span>
                </Button>
              }
            />

            {!limitedMember && (
              <Button type="button" className="footer-button" onClick={handleFleetMessageOpen} sx={footerButtonSx}>
                <span className="icon">
                  <LocalShipping />
                </span>
                <span className="text">Fleet Message</span>
              </Button>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {makeCallButton}
            {makeRecording}
            <Button
              type="button"
              // `disabled` carries the in-flight send, which the draft's own sendability cannot: the text is still
              // there, and still sendable, until the publish returns.
              disabled={!canSendMessage || disabled}
              aria-label={Locator.feed.input.send}
              onClick={sendTextMessage}
              sx={{
                borderRadius: "50%",
                minWidth: 0,
                width: 38,
                height: 38,
                p: 0,
                ...(canSendMessage
                  ? {
                      bgcolor: "var(--color-brand-primary-500)",
                      "&:hover": {
                        bgcolor: "var(--color-brand-primary-600)",
                      },
                    }
                  : {
                      bgcolor: "var(--color-zinc-700)",
                      "&:hover": { bgcolor: "var(--color-zinc-600)" },
                    }),
              }}
            >
              <PaperAirplane
                style={{
                  fill: !canSendMessage ? theme.palette.neutral.main : theme.palette.primary.main,
                }}
              />
            </Button>
          </Box>
        </Box>
      </Box>
      <TemplatesModal open={templatesModal} useSelectedTemplate={handleTemplateSelection} />
    </>
  );
}
